import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, XCircle } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling");

  useEffect(() => {
    if (!sessionId) return setState("error");
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState("paid");
        if (data.status === "failed" || data.status === "expired") return setState("error");
      } catch {
        return setState("error");
      }
      if (++attempts < 12) setTimeout(poll, 2000);
      else setState("error");
    };
    poll();
  }, [sessionId]);

  return (
    <AuthLayout eyebrow="Checkout" title={null}>
      <div data-testid="payment-success-panel" className="glass-panel p-8 sm:p-12 text-center mt-8">
        {state === "polling" && (
          <>
            <Loader2 className="mx-auto text-gold mb-5 animate-spin" size={36} strokeWidth={1.25} />
            <h2 className="font-serif text-2xl text-amber-100/95 mb-2">Confirming your payment…</h2>
            <p className="text-sm text-neutral-400 font-light">One moment while we settle everything.</p>
          </>
        )}
        {state === "paid" && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <ShieldCheck className="mx-auto text-gold mb-5" size={40} strokeWidth={1.25} />
            <h2 className="font-serif text-3xl text-amber-100/95 mb-3">
              Welcome to the <span className="italic gold-text">Society</span>
            </h2>
            <p className="text-sm text-neutral-400 font-light leading-relaxed mb-8">
              Payment confirmed. Your membership is now active on your account — the full directory is yours to explore.
            </p>
            <Link to="/" data-testid="payment-success-home-link" className="inline-block px-8 py-3 bg-gold text-obsidian text-xs uppercase tracking-[0.2em] font-medium hover:bg-gold-bright transition-colors">
              Enter the Directory
            </Link>
          </motion.div>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto text-red-400 mb-5" size={36} strokeWidth={1.25} />
            <h2 className="font-serif text-2xl text-amber-100/95 mb-2">We couldn't confirm that payment</h2>
            <p className="text-sm text-neutral-400 font-light mb-8">
              If your card was charged, contact info@monroesociety.com and we'll make it right.
            </p>
            <Link to="/membership" data-testid="payment-retry-link" className="inline-block px-8 py-3 border hairline-gold text-xs uppercase tracking-[0.2em] text-amber-100/90 hover:border-amber-400/60 transition-colors">
              Back to Membership
            </Link>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
